import Papa from "papaparse";
import type { CsvParseResult, CsvParser, CsvRow } from "../../application/ports/csv.ts";
import { isWordType } from "../../domain/vocabulary/types.ts";
import { normalizeWordInput } from "../../domain/vocabulary/normalize.ts";

const MAX_ROWS = 200;
const WORD_HEADERS = ["word", "phrase", "english", "term"];
const CONTEXT_HEADERS = ["context", "sentence", "example"];
const DEFINITION_HEADERS = ["definition", "meaning"];
const HINT_HEADERS = ["hint", "russian", "russian hint", "russian_hint"];
const TYPE_HEADERS = ["type", "pos", "part of speech", "tag"];

function normalizeHeader(value: string): string {
  return value.replace(/^\uFEFF/, "").trim().toLowerCase();
}

function pick(row: Record<string, string>, aliases: string[]): string | undefined {
  for (const alias of aliases) {
    const key = Object.keys(row).find((item) => normalizeHeader(item) === alias);
    if (key && row[key]?.trim()) {
      return row[key].trim();
    }
  }
  return undefined;
}

export class PapaCsvParser implements CsvParser {
  parse(fileText: string): CsvParseResult {
    const parsed = Papa.parse<Record<string, string>>(fileText.replace(/^\uFEFF/, ""), {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header: string) => header.trim(),
    });
    const headerErrors: string[] = [];
    const fields = (parsed.meta.fields ?? []).map((field) => normalizeHeader(field));
    if (new Set(fields).size !== fields.length) {
      headerErrors.push("The file has duplicate column names.");
    }
    const hasWord = fields.some((field) => WORD_HEADERS.includes(field));
    if (!hasWord) {
      headerErrors.push("A word column is required. Use a header such as “word”.");
    }
    if (parsed.errors.length > 0) {
      headerErrors.push(...parsed.errors.slice(0, 8).map((error) => error.message));
    }
    const rows: CsvRow[] = [];
    let truncated = false;
    parsed.data.forEach((record, index) => {
      if (rows.length >= MAX_ROWS) {
        truncated = true;
        return;
      }
      const word = pick(record, WORD_HEADERS) ?? "";
      const normalized = normalizeWordInput(word);
      const type = pick(record, TYPE_HEADERS);
      const errors: string[] = [];
      if (!normalized.normalizedText) {
        errors.push("Missing word.");
      }
      if (type && !isWordType(type.toLowerCase().replace(/\s+/g, "_"))) {
        errors.push(`Unknown type “${type}”.`);
      }
      rows.push({
        rowNumber: index + 2,
        word: normalized.displayText,
        context: pick(record, CONTEXT_HEADERS),
        definition: pick(record, DEFINITION_HEADERS),
        russianHint: pick(record, HINT_HEADERS),
        type: type ? type.toLowerCase().replace(/\s+/g, "_") : undefined,
        errors,
      });
    });
    return { rows, headerErrors, truncated };
  }
}
